import { memo, useState, FC, useEffect, useRef, useMemo } from "react";
import {
  Box,
  SxProps,
  Theme,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";

import { useBanners } from "../../../store/contexts/BannersContext/useBanners";
import { EBannerLocale } from "../../../models/banners";
import HomeBannersCarousel from "../BannersCarousel";

interface IMediaFile {
  mediaType?: { code?: string };
  cdnDomain: string;
  filePath: string;
}

interface IBanner {
  id: string | number;
  targetingLink?: string;
  alternativeText: string;
  bannerMediaFiles?: IMediaFile[];
}

interface IProcessedBanner extends IBanner {
  path: string;
  displayMediaFile: IMediaFile | undefined;
  width: number;
  height: number;
}

interface IScreenBanners {
  localeCode: EBannerLocale;
  sx?: SxProps<Theme>;
}

const DynamicSizeBanners: FC<IScreenBanners> = ({ localeCode, sx }) => {
  const { banners } = useBanners({ localeCode });
  const { breakpoints }: Theme = useTheme();
  const isDownMd: boolean = useMediaQuery(breakpoints.down("md"));
  const [currentMaxHeight, setCurrentMaxHeight] = useState<number>(0);

  const [groupedBanners, setGroupedBanners] = useState<IProcessedBanner[][]>(
    []
  );
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const carouselContainerRef = useRef<HTMLDivElement>(null);

  const stringifiedBanners = useMemo(() => JSON.stringify(banners), [banners]);

  useEffect(() => {
    const measureAndGroupBanners = async (
      bannersToProcess: IBanner[]
    ): Promise<void> => {
      const containerWidth = carouselContainerRef.current?.clientWidth;
      if (!containerWidth) return;

      setIsLoading(true);

      const bannersWithDimensionsPromises = bannersToProcess.map((banner) => {
        return new Promise<IProcessedBanner>((resolve) => {
          let displayMediaFile: IMediaFile | undefined;
          const mobileMediaFile = banner.bannerMediaFiles?.find(
            (f) => f.mediaType?.code === "2"
          );
          const desktopMediaFile = banner.bannerMediaFiles?.find(
            (f) => f.mediaType?.code === "1"
          );

          if (isDownMd && mobileMediaFile) displayMediaFile = mobileMediaFile;
          else if (desktopMediaFile) displayMediaFile = desktopMediaFile;
          else displayMediaFile = banner.bannerMediaFiles?.[0];

          const path = displayMediaFile
            ? `${displayMediaFile.cdnDomain}/${displayMediaFile.filePath}`
            : "";

          if (!path) {
            return resolve({
              ...banner,
              path,
              displayMediaFile,
              width: 0,
              height: 0,
            });
          }

          const img = new Image();
          img.src = path;
          img.onload = () =>
            resolve({
              ...banner,
              path,
              displayMediaFile,
              width: img.naturalWidth,
              height: img.naturalHeight,
            });
          img.onerror = () =>
            resolve({ ...banner, path, displayMediaFile, width: 0, height: 0 });
        });
      });

      const measuredBanners = await Promise.all(bannersWithDimensionsPromises);
      const bannersToDisplay = measuredBanners.filter(
        (b) => b.path && b.width > 0
      );

      const finalGroups: IProcessedBanner[][] = [];
      let currentGroup: IProcessedBanner[] = [];
      let currentGroupWidth: number = 0;
      const gap = 16;

      bannersToDisplay.forEach((banner) => {
        if (banner.width >= containerWidth) {
          if (currentGroup.length > 0) finalGroups.push(currentGroup);
          finalGroups.push([banner]);
          currentGroup = [];
          currentGroupWidth = 0;
          return;
        }

        const widthWithNewBanner =
          currentGroupWidth +
          banner.width +
          (currentGroup.length > 0 ? gap : 0);

        if (widthWithNewBanner > containerWidth && currentGroup.length > 0) {
          finalGroups.push(currentGroup);
          currentGroup = [banner];
          currentGroupWidth = banner.width;
        } else {
          currentGroup.push(banner);
          currentGroupWidth = widthWithNewBanner;
        }
      });

      if (currentGroup.length > 0) finalGroups.push(currentGroup);

      setGroupedBanners(finalGroups);
      setIsLoading(false);
    };

    const bannersParsed = JSON.parse(stringifiedBanners);
    if (bannersParsed.length > 0) {
      measureAndGroupBanners(bannersParsed);
    } else {
      setGroupedBanners([]);
      setIsLoading(false);
    }
  }, [stringifiedBanners, isDownMd]);

  const onClickHandler = (banner: IProcessedBanner): void => {
    if (banner.targetingLink) {
      window.open(banner.targetingLink, "_blank");
    }
  };
  
  useEffect(() => {
    if(!groupedBanners || !groupedBanners.length) return;
    const { height } = groupedBanners[currentSlide].reduce(
      (maxHeight, current) =>
        current.height > maxHeight.height ? current : maxHeight
    );
    if (height > 400) {
      setCurrentMaxHeight(400);
      return;
    }
    setCurrentMaxHeight(height);
  }, [groupedBanners, setCurrentMaxHeight, currentSlide]);

  return (
    <Box
      width="100%"
      sx={{
        ...sx,
        "& .carousel .slide": {
          height: groupedBanners.length > 1 ? `${currentMaxHeight + 44}px` : "auto",
          paddingX: groupedBanners.length > 1 ? 6 : 0,
        },
      }}
      ref={carouselContainerRef}
    >
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : (
        <HomeBannersCarousel
          currentSlide={currentSlide}
          changeSlide={(index: number) => setCurrentSlide(index)}
          autoPlay={false}
          hasDynamicHeight={false}
        >
          {groupedBanners.map((bannerGroup, groupIndex) => {
            return (
              <Box
                key={groupIndex}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  transition: "height 0.3s ease-in-out",
                }}
              >
                {bannerGroup.map((banner) => {
                  return (
                    <Box
                      key={banner.id}
                      onClick={() => onClickHandler(banner)}
                      sx={{
                        "&:hover": {
                          cursor: banner.targetingLink ? "pointer" : "default",
                          opacity: 0.9,
                        },
                      }}
                    >
                      <img
                        src={banner.path}
                        alt={banner.alternativeText}
                        style={{
                          display: "block",
                          maxWidth: "100%",
                          maxHeight: "400px",
                          height: "auto",
                          width: "auto",
                          borderRadius: 10,
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </HomeBannersCarousel>
      )}
    </Box>
  );
};

export default memo(DynamicSizeBanners);
